import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import { fileValidaterFunction, fetchJobDetails } from 'actions/homePage';
import './UploadUI.scss';
import { serviceUrl } from 'api/config';
import {
  CoreButton,
  CoreNotification,
  CoreTextfield,
  CoreUploader
} from '@komodo-design-system/komodo-components-react';
import LinkButton from 'components/SharedComponents/LinkButton/LinkButton';

const UploadUI = ({setIsLoading}) => {
  const [files, setFiles] = useState([]);
  const [jobName, setJobName] = useState('');
  const dispatch = useDispatch();
  const [isSuccessAlert, setIsSuccessAlert] = useState(false);
  const [isErrorAlert, setIsErrorAlert] = useState(false);
  const [isErrorFileType, setIsErrorFileType] = useState(false);
  const [errorDetails, setErrorDetails] = useState('');
  const {userName} = useSelector((state) => state.userTypeReducer?.userType?.data) || {};

  const uploadFile = (event) => {
    if (event.detail.length === 0) {
      return;
    }
    const detail = event.detail[0];
    const file = detail.fileReference;
    if (file.name?.includes('xlsx')) {
      detail.status = 'completed';
      setFiles([detail]);
    } else {
      setIsErrorFileType(true);
      setFiles([]);
    }
  };

  const closeFileCallback = () => {
    setFiles([]);
  };

  const handleError = (response) => {
    const message =
      response?.message ||
      'The PubMed ID, Article Title, or Article external link may be missing or incorrect. Please review and submit.';
    setErrorDetails(message);
    setIsErrorAlert(true);
  };

  const handleSuccess = () => {
    setIsSuccessAlert(true);
    setErrorDetails('');
  };

  const submitFile = (e) => {
    e.preventDefault();
    setIsLoading(true);
    const formData = new FormData();
    formData.append('uploadFile', files[0].fileReference);
    formData.append('jobName', jobName);
    formData.append('userId', userName);
    dispatch(fileValidaterFunction(formData))
      .then((res) => {
        dispatch(fetchJobDetails(userName, 0));
        res?.isSuccess === true ? handleSuccess() : handleError(res);
        setFiles([]);
        setJobName('');
        setIsLoading(false);
      })
      .catch((error) => {
        setIsLoading(false);
        console.log('error check', error);
      });
  };

  const downLoadPdf = () => {
    setIsLoading(true);
    // using Java Script method to get PDF file
    const filePath = serviceUrl?.includes('qa')
      ? 'CitationBulkUpload_QA.xlsx'
      : 'CitationBulkUpload.xlsx';
    fetch(filePath).then((response) => {
      response.blob().then((blob) => {
        // Creating new object of PDF file
        setIsLoading(false);
        const fileURL = window.URL.createObjectURL(blob);
        // Setting various property values
        let alink = document.createElement('a');
        alink.href = fileURL;
        alink.download = filePath;
        alink.click();
      });
    });
  };

  const handleSuccessOnClose = () => {
    setIsSuccessAlert(false);
  };

  const handleErrorOnClose = () => {
    setIsErrorAlert(false);
  };

  const handleFileTyeErrorOnClose = () => {
    setIsErrorFileType(false);
  };

  const clearUploadData = () => {
    setJobName('');
    setFiles([]);
    setErrorDetails();
  };

  return (
    <div className="blk-uploadUi">
      <CoreUploader
        files={files}
        limitone
        showicon
        variant="full-size"
        onFileListUpdated={uploadFile}
        closeFileCallback={closeFileCallback}></CoreUploader>

      <p className="blk-uploadUi__title-div">
        <span className="blk-uploadUi__upload-title">
          <FormattedMessage id="uploadExelFile" />
        </span>
        <LinkButton
          label="downloadable template"
          onClickHandler={() => downLoadPdf()}
        />
      </p>
      <div className="blk-uploadUi__file-error">
        {isErrorFileType && (
          <CoreNotification
            label="Error: "
            message="Please select xlsx file only"
            variant="toast"
            state="error"
            closed={false}
            timer={4000}
            onNotificationClosed={handleFileTyeErrorOnClose}
            reusable></CoreNotification>
        )}
      </div>
      <div className="blk-uploadUi__job-label_div">
        <div className="job-name">
          <label for="jobName" className="uploadUi__job-label required-field">
            Job Name
          </label>
          <CoreTextfield
            type="text"
            required
            value={jobName}
            onTextfieldChange={(e) =>
              setJobName(e.target.value)
            }></CoreTextfield>
        </div>
        <div className="action-buttons">
          <CoreButton
            label="Submit"
            type="button"
            variant="primary"
            size="large"
            noradius="none"
            onClick={(e) => submitFile(e)}
            disabled={files.length === 0 || !jobName?.trim() ? true : false}>
            Submit
          </CoreButton>
          <CoreButton
            label="Reset"
            type="button"
            variant="outline"
            size="large"
            noradius="none"
            onClick={() => clearUploadData()}>
            <div slot="icon">
              <span className="icon-16-repeat-bold reset-icon"></span>
            </div>
          </CoreButton>
        </div>

        {isSuccessAlert && (
          <CoreNotification
            label="Success: "
            message="File uploaded successfully"
            variant="toast"
            state="success"
            closed={!isSuccessAlert}
            timer={4000}
            onNotificationClosed={handleSuccessOnClose}></CoreNotification>
        )}
        {isErrorAlert && (
          <CoreNotification
            label="Error: "
            message={errorDetails}
            variant="toast"
            state="error"
            closed={!isErrorAlert && !errorDetails}
            timer={4000}
            onNotificationClosed={handleErrorOnClose}
            reusable></CoreNotification>
        )}
      </div>
    </div>
  );
};

UploadUI.propTypes = {
  setIsLoading: PropTypes.func.isRequired,
};

export default UploadUI;
